import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from '../assets/UserRegister.module.css';
import Modal from '../components/Modal';
import BeforeYouApplyModal from '../components/BeforeYouApplyModal';

// Image assets from Figma
const imgImage3 = "/images/icons/user-register-bg.png";
const img = "/images/icons/back-arrow.svg";
const img1 = "/images/icons/back-arrow.svg";
const imgLogo = "/images/icons/logo.svg";
const img2 = "/images/icons/dropdown-arrow.svg";
const img3 = "/images/icons/checkbox-unchecked.svg";
const img4 = "/images/icons/checkbox-checked.svg";
const img5 = "/images/icons/google-icon.svg";
const img6 = "/images/icons/info-icon.svg";
const imgVector = "/images/icons/vector-bg.svg";
const img7 = "/images/icons/google-signin.svg";
const img8 = "/images/icons/chevron-down.svg";
const img9 = "/images/icons/eye-icon.svg";
const imgVector1 = "/images/icons/vector-bg-2.svg";

// Where the marketing source slug is kept between landing on the page and
// submitting the form, so it survives a reload or a detour to the info modal.
const REGISTRATION_SOURCE_KEY = 'registrationSource';

const UserRegister = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setToken, setIsAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dependants: 0, // 0 = no, 1 = yes
    partner: 0, // 0 = no, 1 = yes
    partnerEmail: '',
    termsAccepted: false,
    petsPolicyAccepted: false,
    ageConfirmed: false
  });

  const [errors, setErrors] = useState({});

  // Capture the trackable registration link the visitor arrived on
  // (e.g. /register?source=spareroom-ads) and remember it for this session.
  // The backend normalises unknown values to "direct".
  useEffect(() => {
    const source = searchParams.get('source') || searchParams.get('utm_source');
    if (source) {
      try {
        sessionStorage.setItem(REGISTRATION_SOURCE_KEY, source);
      } catch (err) {
        console.warn('Could not persist registration source:', err);
      }
    }
  }, [searchParams]);

  const getRegistrationSource = () => {
    const fromUrl = searchParams.get('source') || searchParams.get('utm_source');
    if (fromUrl) return fromUrl;
    try {
      return sessionStorage.getItem(REGISTRATION_SOURCE_KEY) || '';
    } catch (err) {
      return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Clear field-specific errors when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Convert dependants and partner values to numbers
    let processedValue = value;
    if (name === 'dependants' || name === 'partner') {
      // Convert string "1" or "0" to actual numbers, or keep empty string as 0
      processedValue = value === '' ? 0 : parseInt(value, 10);
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    // Basic validation
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.termsAccepted) newErrors.termsAccepted = 'You must accept the terms';
    if (!formData.petsPolicyAccepted) newErrors.petsPolicyAccepted = 'You must confirm the pets policy';
    if (!formData.ageConfirmed) newErrors.ageConfirmed = 'You must confirm your age';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    try {
      // Prepare data for backend API
      const registrationData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        dependants: formData.dependants,
        partner: formData.partner,
        partnerEmail: formData.partnerEmail,
        termsAccepted: formData.termsAccepted,
        petsPolicyAccepted: formData.petsPolicyAccepted,
        ageConfirmed: formData.ageConfirmed,
        registration_source: getRegistrationSource()
      };

      console.log('Sending user registration data:', registrationData);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/auth/user-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User registration response:', data);

        // Source has been recorded against the guardian - don't let it carry
        // over to another registration in the same browser session.
        try {
          sessionStorage.removeItem(REGISTRATION_SOURCE_KEY);
        } catch (err) {
          // Non-fatal - storage may be unavailable in private browsing
        }

        // Laravel behavior: auto-login after registration
        console.log('🔍 Checking auto-login conditions:', {
          auto_login: data.auto_login,
          has_token: !!data.token,
          has_user: !!data.user
        });
        
        if (data.auto_login && data.token) {
          console.log('✅ Starting auto-login process...');
          
          try {
            // Authenticate the user immediately (Laravel equivalent)
            console.log('🔐 Setting authentication state...');
            setUser(data.user);
            setToken(data.token);
            setIsAuthenticated(true);
            console.log('✅ Auth state set in context');
            
            // Store in localStorage using authService
            console.log('💾 Storing auth data in localStorage...');
            const authService = await import('../services/authService');
            authService.default.setStoredAuth(data.token, data.user);
            console.log('✅ Auth data stored in localStorage');
            
            console.log('🎉 User automatically logged in:', data.user.email);
            
            // Store refresh token if provided
            if (data.refreshToken) {
              localStorage.setItem('refreshToken', data.refreshToken);
              console.log('✅ Refresh token stored');
            }
            
            // Redirect to dashboard with success message
            console.log('🚀 Attempting to navigate to dashboard...');
            
            // Small delay to ensure state is set before navigation
            setTimeout(() => {
              navigate('/dashboard', { 
                state: { 
                  message: data.message || 'Account created successfully! Welcome to Global Guardians.',
                  type: 'success'
                } 
              });
              console.log('✅ Navigation called successfully');
            }, 100);
            
          } catch (error) {
            console.error('❌ Error during auto-login process:', error);
            // Fallback to login page if auto-login fails
            navigate('/login', { 
              state: { 
                message: 'Account created successfully! Please login to continue.',
                type: 'success',
                email: formData.email,
                password: formData.password // Pre-fill password too
              } 
            });
          }
        } else {
          console.warn('⚠️ Auto-login conditions not met:', {
            auto_login: data.auto_login,
            has_token: !!data.token,
            message: 'Redirecting to login page'
          });
          // Fallback: redirect to login if auto-login failed
          navigate('/login', { 
            state: { 
              message: data.message || 'Account created successfully! Please login to continue.',
              type: 'success',
              email: formData.email, // Pre-fill login form
              password: formData.password // Pre-fill password too
            } 
          });
        }
      } else {
        // Handle validation errors
        const data = await response.json();
        if (data.errors) {
          setErrors(data.errors);
        } else {
          // Map specific error messages to fields
          const errorMessage = data.message || 'Registration failed. Please try again.';
          
          if (errorMessage.toLowerCase().includes('email already exists')) {
            setErrors({ email: [errorMessage] });
          } else if (errorMessage.toLowerCase().includes('passwords do not match')) {
            setErrors({ confirmPassword: [errorMessage] });
          } else if (errorMessage.toLowerCase().includes('password') && errorMessage.toLowerCase().includes('too short')) {
            setErrors({ password: [errorMessage] });
          } else if (errorMessage.toLowerCase().includes('missing required fields')) {
            setErrors({ general: 'Please fill in all required fields.' });
          } else if (errorMessage.toLowerCase().includes('invalid email format')) {
            setErrors({ email: [errorMessage] });
          } else if (errorMessage.toLowerCase().includes('phone number')) {
            setErrors({ phone: [errorMessage] });
          } else {
            setErrors({ general: errorMessage });
          }
        }
      }
    } catch (err) {
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Implement Google sign-in logic here
    console.log('Google sign-in clicked');
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logoSection}>
            <div
              className={styles.logoImage}
              style={{ backgroundImage: `url('${imgImage3}')` }}
            />
          </div>

          <div className={styles.navButtons}>
            {/* <div className={styles.navItem}>
              <span>Guardians</span>
            </div>
            <div className={styles.navItem}>
              <span>Property Owners</span>
            </div>
            <div className={styles.navItem}>
              <span>About</span>
              <div className={styles.chevronDown}>
                <img src={img} alt="chevron down" />
              </div>
            </div> */}
          </div>

          <div className={styles.authButtons}>
            {/* <div className={styles.signInButton}>
              <span>Sign in</span>
              <div className={styles.userIcon}>
                <img src={img1} alt="user" />
              </div>
            </div>
            <div className={styles.createAccountButton}>
              <span>Create Account</span>
            </div> */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>

  {/* Background Vector */}
  <div className={styles.backgroundVector}>
        <img src={imgVector1} alt="background vector" />
      </div>

        <div className={styles.topIconsSection}>
          <div className={styles.backButton} onClick={handleBackClick}>
            <div className={styles.backIcon}>
              <img src={img6} alt="back" />
            </div>
          </div>
          <div className={styles.vectorImage}>
            <img src={imgVector} alt="vector" />
          </div>
        </div>
        <div className={styles.contentWrapper}>
          <div className={styles.twoColumnLayout}>
            {/* Left Column - Form */}
            <div className={styles.leftColumn}>
              <div className={styles.formContainer}>
                <div className={styles.formHeader}>
                  <h1 className={styles.formTitle}>Ready to become a Guardian?</h1>
                  <p className={styles.formDescription}>
                    Register today to start exploring the benefits of becoming a guardian and to book in for viewings.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className={styles.registrationForm}>
                <div className={styles.formFields}>
                  {/* First Name */}
                  <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel}>First name</label>
                    <div className={styles.inputContainer}>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={styles.formInput}
                        placeholder=""
                      />
                    </div>
                    {errors.firstName && <span className={styles.errorText}>{Array.isArray(errors.firstName) ? errors.firstName[0] : errors.firstName}</span>}
                  </div>

                  {/* Last Name */}
                  <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel}>Last name</label>
                    <div className={styles.inputContainer}>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={styles.formInput}
                        placeholder=""
                      />
                    </div>
                    {errors.lastName && <span className={styles.errorText}>{Array.isArray(errors.lastName) ? errors.lastName[0] : errors.lastName}</span>}
                  </div>

                  {/* Email */}
                  <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel}>Email address</label>
                    <div className={styles.inputContainer}>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={styles.formInput}
                        placeholder=""
                      />
                    </div>
                    {errors.email && <span className={styles.errorText}>{Array.isArray(errors.email) ? errors.email[0] : errors.email}</span>}
                  </div>

                  {/* Phone */}
                  <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel}>Phone number</label>
                    <div className={styles.inputContainer}>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={styles.formInput}
                        placeholder=""
                      />
                    </div>
                    {errors.phone && <span className={styles.errorText}>{Array.isArray(errors.phone) ? errors.phone[0] : errors.phone}</span>}
                  </div>

                  {/* Password */}
                  <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel}>Password</label>
                    <div className={styles.inputContainer}>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className={styles.formInput}
                        placeholder=""
                      />
                    </div>
                    {errors.password && <span className={styles.errorText}>{Array.isArray(errors.password) ? errors.password[0] : errors.password}</span>}
                  </div>

                  {/* Confirm Password */}
                  <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel}>Confirm password</label>
                    <div className={styles.inputContainer}>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className={styles.formInput}
                        placeholder=""
                      />
                    </div>
                    {errors.confirmPassword && <span className={styles.errorText}>{Array.isArray(errors.confirmPassword) ? errors.confirmPassword[0] : errors.confirmPassword}</span>}
                  </div>

                  {/* Dependants */}
                  <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel}>Do you have any dependants?</label>
                    <div className={styles.selectContainer}>
                      <select
                        name="dependants"
                        value={formData.dependants}
                        onChange={handleInputChange}
                        className={styles.formSelect}
                      >
                        <option value="">Not specified</option>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </select>
                      <div className={styles.selectIcon}>
                        <img src={img} alt="dropdown" />
                      </div>
                    </div>
                  </div>

                  {/* Dependants Error Message */}
                  {formData.dependants === 1 && (
                    <div className={styles.fieldRow}>
                      <div className={styles.eligibilityError}>
                        <strong>Unfortunately you're not eligible</strong>
                        <br />
                        We don't accept guardianship for anyone who has dependants.
                      </div>
                    </div>
                  )}

                  {/* Partner */}
                  <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel}>Do you plan to move in with a partner, spouse, or someone you know?</label>
                    <div className={styles.selectContainer}>
                      <select
                        name="partner"
                        value={formData.partner}
                        onChange={handleInputChange}
                        className={styles.formSelect}
                      >
                        <option value="">Not specified</option>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </select>
                      <div className={styles.selectIcon}>
                        <img src={img} alt="dropdown" />
                      </div>
                    </div>
                  </div>

                  {/* Partner Email - Show when partner is Yes */}
                  {formData.partner === 1 && (
                    <div className={styles.fieldRow}>
                      <div className={styles.partnerEmailSection}>
                        <p className={styles.partnerEmailText}>
                          Please enter the email address of the person you plan to move in with? 
                          They must also apply separately.
                        </p>
                        <div className={styles.partnerEmailContainer}>
                          <label className={styles.partnerEmailLabel}>Their email address</label>
                          <input
                            type="email"
                            name="partnerEmail"
                            value={formData.partnerEmail}
                            onChange={handleInputChange}
                            className={styles.partnerEmailInput}
                            placeholder="Please specify"
                          />
                          {errors.partnerEmail && <span className={styles.errorText}>{Array.isArray(errors.partnerEmail) ? errors.partnerEmail[0] : errors.partnerEmail}</span>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Checkboxes */}
                <div className={styles.checkboxesContainer}>
                  <div className={styles.checkboxItem}>
                    <div className={`${styles.checkbox} ${formData.termsAccepted ? styles.checked : ''}`}>
                      <input
                        type="checkbox"
                        name="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleInputChange}
                        className={styles.checkboxInput}
                      />
                    </div>
                    <span className={styles.checkboxText}>
                      I confirm that I have read the <span className={styles.underline}>Terms of Service</span> and <span className={styles.underline}>Privacy Policy</span>
                    </span>
                    {errors.termsAccepted && <span className={styles.errorText}>{Array.isArray(errors.termsAccepted) ? errors.termsAccepted[0] : errors.termsAccepted}</span>}
                  </div>

                  <div className={styles.checkboxItem}>
                    <div className={`${styles.checkbox} ${formData.petsPolicyAccepted ? styles.checked : ''}`}>
                      <input
                        type="checkbox"
                        name="petsPolicyAccepted"
                        checked={formData.petsPolicyAccepted}
                        onChange={handleInputChange}
                        className={styles.checkboxInput}
                      />
                    </div>
                    <span className={styles.checkboxText}>
                      I confirm that I understand that no pets are allowed in Guardian properties
                    </span>
                    {errors.petsPolicyAccepted && <span className={styles.errorText}>{Array.isArray(errors.petsPolicyAccepted) ? errors.petsPolicyAccepted[0] : errors.petsPolicyAccepted}</span>}
                  </div>

                  <div className={styles.checkboxItem}>
                    <div className={`${styles.checkbox} ${formData.ageConfirmed ? styles.checked : ''}`}>
                      <input
                        type="checkbox"
                        name="ageConfirmed"
                        checked={formData.ageConfirmed}
                        onChange={handleInputChange}
                        className={styles.checkboxInput}
                      />
                    </div>
                    <span className={styles.checkboxText}>
                      I confirm that I am over the age of 21
                    </span>
                    {errors.ageConfirmed && <span className={styles.errorText}>{Array.isArray(errors.ageConfirmed) ? errors.ageConfirmed[0] : errors.ageConfirmed}</span>}
                  </div>
                </div>

                {/* General Error Display */}
                {errors.general && (
                  <div className={styles.generalError}>{errors.general}</div>
                )}

                {/* Action Buttons */}
                <div className={styles.actionButtons}>
                  <button 
                    type="submit" 
                    className={styles.continueButton} 
                    disabled={isLoading || formData.dependants === 1}
                  >
                    {isLoading ? 'Creating Account...' : 'Continue'}
                  </button>

                  {/* Google Sign In Button - Hidden */}
                  {/* <div className={styles.googleButton} onClick={handleGoogleSignIn}>
                    <div className={styles.googleIcon}>
                      <img src={img7} alt="Google" />
                    </div>
                    <span>Sign in with Google</span>
                  </div> */}

                  <div className={styles.signInLink}>
                    <span className={styles.signInText}>
                      Already have an account? <span className={styles.underline}>Sign in</span>
                    </span>
                  </div>
                </div>
              </form>
              </div>
            </div>

            {/* Right Column - Info Panel */}
            <div className={styles.rightColumn}>
              <div className={styles.infoPanel}>
                <div className={styles.infoContent} onClick={() => setIsInfoOpen(true)} style={{ cursor: 'pointer' }}>
                  <div className={styles.infoAccent}></div>
                  <div className={styles.infoContentInner}>
                    <div className={styles.infoHeader}>
                      <div className={styles.infoIcon}>
                        <img src={img8} alt="info" />
                      </div>
                      <span className={styles.infoTitle}>Before you apply</span>
                      <div className={styles.infoChevron}>
                        <img src={img9} alt="chevron right" />
                      </div>
                    </div>
                    <p className={styles.infoDescription}>
                      Please review this information before you create your account. This ensures that you are the right candidate for guardianship.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerMain}>
            <div className={styles.footerLogo}>
              <img src={imgLogo} alt="Global Guardians" />
            </div>

            <div className={styles.footerLinks}>
              <div className={styles.linkColumn}>
                <h4>Useful Links</h4>
                <a href="#">About us</a>
                <a href="#">Careers</a>
                <a href="#">Contact</a>
              </div>

              <div className={styles.linkColumn}>
                <h4>Useful Links</h4>
                <a href="#">Property Owners</a>
                <a href="#">Guardians</a>
                <a href="#">Register as a Guardian</a>
                <a href="#">Download our brochure</a>
              </div>

              <div className={styles.linkColumn}>
                <h4>Useful Links</h4>
                <a href="#">Diversity & Inclusion</a>
                <a href="#">Privacy Policy</a>
              </div>

              <div className={styles.socialColumn}>
                <h4>Connect with us</h4>
                <div className={styles.socialIcons}>
                  <div className={styles.socialIcon}>
                    <img src={img2} alt="Instagram" />
                  </div>
                  <div className={styles.socialIcon}>
                    <img src={img3} alt="Facebook" />
                  </div>
                  <div className={styles.socialIcon}>
                    <img src={img4} alt="LinkedIn" />
                  </div>
                  <div className={styles.socialIcon}>
                    <img src={img5} alt="Twitter" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.footerCopyright}>
            <p>Copyright 2025 Global Guardians. All rights reserved</p>
          </div>
        </div>
      </div>
      {/* Before you apply Modal (dedicated) */}
      <BeforeYouApplyModal open={isInfoOpen} onClose={() => setIsInfoOpen(false)} />
    </div>
  );
};

export default UserRegister;
